/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {Type} from '@angular/core';
import {BehaviorSubject} from 'rxjs/BehaviorSubject';
import {Observable} from 'rxjs/Observable';
import {map} from 'rxjs/operator/map';

import {Data, ResolveData, Route} from './config';
import {PRIMARY_OUTLET, ParamMap, Params, convertToParamMap} from './shared';
import {UrlSegment, UrlSegmentGroup, UrlTree, equalSegments} from './url_tree';
import {shallowEqual, shallowEqualArrays} from './utils/collection';
import {Tree, TreeNode} from './utils/tree';
/**
 * \@whatItDoes Represents the state of the router.
 * 
 * \@howToUse 
 * 
 * ```
 * \@Component({templateUrl:'template.html'}) 
 * class MyComponent {
 *   constructor(router: Router) {
 *     const state: RouterState = router.routerState;
 *     const root: ActivatedRoute = state.root;
 *     const child = root.firstChild;
 *     const id: Observable<string> = child.params.map(p => p.id);
 *     //...
 *   }
 * }
 * ```
 * 
 * \@description 
 * RouterState is a tree of activated routes. Every node in this tree knows about the "consumed" URL
 * segments, the extracted parameters, and the resolved data.
 * 
 * See {\@link ActivatedRoute} for more information.
 * 
 * \@stable
 */
export class RouterState extends Tree<ActivatedRoute> {
/**
 * \@internal
 * @param {?} root
 * @param {?} snapshot
 */
constructor(
      root: TreeNode<ActivatedRoute>,
public snapshot: RouterStateSnapshot) {
    super(root);
    setRouterState(<RouterState>this, root);
  }
/**
 * @return {?}
 */
toString(): string { return this.snapshot.toString(); }
}

function RouterState_tsickle_Closure_declarations() {
/**
 * The current snapshot of the router state
 * @type {?}
 */
RouterState.prototype.snapshot;
}

/**
 * @param {?} urlTree
 * @param {?} rootComponent
 * @return {?}
 */
export function createEmptyState(urlTree: UrlTree, rootComponent: Type<any>| null): RouterState {
  const /** @type {?} */ snapshot = createEmptyStateSnapshot(urlTree, rootComponent);
  const /** @type {?} */ emptyUrl = new BehaviorSubject([new UrlSegment('', {})]);
  const /** @type {?} */ emptyParams = new BehaviorSubject({});
  const /** @type {?} */ emptyData = new BehaviorSubject({});
  const /** @type {?} */ emptyQueryParams = new BehaviorSubject({});
  const /** @type {?} */ fragment = new BehaviorSubject('');
  const /** @type {?} */ activated = new ActivatedRoute(
      emptyUrl, emptyParams, emptyQueryParams, fragment, emptyData, PRIMARY_OUTLET, rootComponent,
      snapshot.root);
  activated.snapshot = snapshot.root;
  return new RouterState(new TreeNode<ActivatedRoute>(activated, []), snapshot);
}
/**
 * @param {?} urlTree
 * @param {?} rootComponent
 * @return {?}
 */
export function createEmptyStateSnapshot(
    urlTree: UrlTree, rootComponent: Type<any>| null): RouterStateSnapshot {
  const /** @type {?} */ emptyParams = {};
  const /** @type {?} */ emptyData = {};
  const /** @type {?} */ emptyQueryParams = {};
  const /** @type {?} */ fragment = '';
  const /** @type {?} */ activated = new ActivatedRouteSnapshot(
      [], emptyParams, emptyQueryParams, fragment, emptyData, PRIMARY_OUTLET, rootComponent, null,
      urlTree.root, -1, {});
  return new RouterStateSnapshot('', new TreeNode<ActivatedRouteSnapshot>(activated, []));
}
/**
 * \@whatItDoes Contains the information about a route associated with a component loaded in an
 * outlet.
 * An `ActivatedRoute` can also be used to traverse the router state tree.
 * 
 * \@howToUse 
 * 
 * ```
 * \@Component({...}) 
 * class MyComponent {
 *   constructor(route: ActivatedRoute) {
 *     const id: Observable<string> = route.params.map(p => p.id);
 *     const url: Observable<string> = route.url.map(segments => segments.join(''));
 *     // route.data includes both `data` and `resolve`
 *     const user = route.data.map(d => d.user);
 *   }
 * }
 * ```
 * 
 * \@stable
 */
export class ActivatedRoute {
/**
 * The current snapshot of this route
 */
snapshot: ActivatedRouteSnapshot;
/**
 * \@internal
 */
_futureSnapshot: ActivatedRouteSnapshot;
/**
 * \@internal
 */
_routerState: RouterState;
/**
 * \@internal
 */
_paramMap: Observable<ParamMap>;
/**
 * \@internal
 */
_queryParamMap: Observable<ParamMap>;
/**
 * \@internal
 * @param {?} url
 * @param {?} params
 * @param {?} queryParams
 * @param {?} fragment
 * @param {?} data
 * @param {?} outlet
 * @param {?} component
 * @param {?} futureSnapshot
 */
constructor(
public url: Observable<UrlSegment[]>,
public params: Observable<Params>,
public queryParams: Observable<Params>,
public fragment: Observable<string>,
public data: Observable<Data>,
public outlet: string,
public component: Type<any>|string|null, futureSnapshot: ActivatedRouteSnapshot) {
    this._futureSnapshot = futureSnapshot;
  }
/**
 * The configuration used to match this route
 * @return {?}
 */
get routeConfig(): Route|null { return this._futureSnapshot.routeConfig; }
/**
 * The root of the router state
 * @return {?}
 */
get root(): ActivatedRoute { return this._routerState.root; }
/**
 * The parent of this route in the router state tree
 * @return {?}
 */
get parent(): ActivatedRoute|null { return this._routerState.parent(this); }
/**
 * The first child of this route in the router state tree
 * @return {?}
 */
get firstChild(): ActivatedRoute|null { return this._routerState.firstChild(this); }
/**
 * The children of this route in the router state tree
 * @return {?}
 */
get children(): ActivatedRoute[] { return this._routerState.children(this); }
/**
 * The path from the root of the router state tree to this route
 * @return {?}
 */
get pathFromRoot(): ActivatedRoute[] { return this._routerState.pathFromRoot(this); }
/**
 * @return {?}
 */
get paramMap(): Observable<ParamMap> {
    if (!this._paramMap) {
      this._paramMap = map.call(this.params, (p: Params): ParamMap => convertToParamMap(p));
    }
    return this._paramMap;
  }
/**
 * @return {?}
 */
get queryParamMap(): Observable<ParamMap> {
    if (!this._queryParamMap) {
      this._queryParamMap =
          map.call(this.queryParams, (p: Params): ParamMap => convertToParamMap(p));
    }
    return this._queryParamMap;
  }
/**
 * @return {?}
 */
toString(): string {
    return this.snapshot ? this.snapshot.toString() : `Future(${this._futureSnapshot})`;
  }
}

function ActivatedRoute_tsickle_Closure_declarations() {
/**
 * The current snapshot of this route
 * @type {?}
 */
ActivatedRoute.prototype.snapshot;
/**
 * \@internal
 * @type {?}
 */
ActivatedRoute.prototype._futureSnapshot;
/**
 * \@internal
 * @type {?}
 */
ActivatedRoute.prototype._routerState;
/**
 * \@internal
 * @type {?}
 */
ActivatedRoute.prototype._paramMap;
/**
 * \@internal
 * @type {?}
 */
ActivatedRoute.prototype._queryParamMap;
/**
 * An observable of the URL segments matched by this route
 * @type {?}
 */
ActivatedRoute.prototype.url;
/**
 * An observable of the matrix parameters scoped to this route
 * @type {?}
 */
ActivatedRoute.prototype.params;
/**
 * An observable of the query parameters shared by all the routes
 * @type {?}
 */
ActivatedRoute.prototype.queryParams;
/**
 * An observable of the URL fragment shared by all the routes
 * @type {?}
 */
ActivatedRoute.prototype.fragment;
/**
 * An observable of the static and resolved data of this route.
 * @type {?}
 */
ActivatedRoute.prototype.data;
/**
 * The outlet name of the route. It's a constant
 * @type {?}
 */
ActivatedRoute.prototype.outlet;
/** @type {?} */
ActivatedRoute.prototype.component;
}


/** @internal */
export type Inherited = {
  params: Params,
  data: Data,
  resolve: Data,
};
/**
 * \@internal
 * @param {?} route
 * @return {?}
 */
export function inheritedParamsDataResolve(route: ActivatedRouteSnapshot): Inherited {
  const /** @type {?} */ pathToRoot = route.pathFromRoot;

  let /** @type {?} */ inhertingStartingFrom = pathToRoot.length - 1;

  while (inhertingStartingFrom >= 1) {
    const /** @type {?} */ current = pathToRoot[inhertingStartingFrom];
    const /** @type {?} */ parent = pathToRoot[inhertingStartingFrom - 1];
    // current route is an empty path => inherits its parent's params and data
    if (current.routeConfig && current.routeConfig.path === '') {
      inhertingStartingFrom--;

      // parent is componentless => current route should inherit its params and data
    } else if (!parent.component) {
      inhertingStartingFrom--;

    } else {
      break;
    }
  }

  return pathToRoot.slice(inhertingStartingFrom).reduce((res, curr) => {
    const /** @type {?} */ params = {...res.params, ...curr.params};
    const /** @type {?} */ data = {...res.data, ...curr.data};
    const /** @type {?} */ resolve = {...res.resolve, ...curr._resolvedData};
    return {params, data, resolve};
  }, /** @type {?} */(( <any>{params: {}, data: {}, resolve: {}})));
}
/**
 * \@whatItDoes Contains the information about a route associated with a component loaded in an
 * outlet
 * at a particular moment in time. ActivatedRouteSnapshot can also be used to traverse the router
 * state tree.
 * 
 * \@howToUse 
 * 
 * ```
 * \@Component({templateUrl:'./my-component.html'}) 
 * class MyComponent {
 *   constructor(route: ActivatedRoute) {
 *     const id: string = route.snapshot.params.id;
 *     const url: string = route.snapshot.url.join('');
 *     const user = route.snapshot.data.user;
 *   }
 * }
 * ```
 * 
 * \@stable
 */
export class ActivatedRouteSnapshot {
/**
 * \@internal *
 */
_routeConfig: Route|null;
/**
 * \@internal *
 */
_urlSegment: UrlSegmentGroup;
/**
 * \@internal
 */
_lastPathIndex: number;
/**
 * \@internal
 */
_resolve: ResolveData;
/**
 * \@internal
 */
_resolvedData: Data;
/**
 * \@internal
 */
_routerState: RouterStateSnapshot;
/**
 * \@internal
 */
_paramMap: ParamMap;
/**
 * \@internal
 */
_queryParamMap: ParamMap;
/**
 * \@internal
 * @param {?} url
 * @param {?} params
 * @param {?} queryParams
 * @param {?} fragment
 * @param {?} data
 * @param {?} outlet
 * @param {?} component
 * @param {?} routeConfig
 * @param {?} urlSegment
 * @param {?} lastPathIndex
 * @param {?} resolve
 */
constructor(
public url: UrlSegment[],
public params: Params,
public queryParams: Params,
public fragment: string,
public data: Data,
public outlet: string,
public component: Type<any>|string|null, routeConfig: Route|null, urlSegment: UrlSegmentGroup,
      lastPathIndex: number, resolve: ResolveData) {
    this._routeConfig = routeConfig;
    this._urlSegment = urlSegment;
    this._lastPathIndex = lastPathIndex;
    this._resolve = resolve;
  }
/**
 * The configuration used to match this route
 * @return {?}
 */
get routeConfig(): Route|null { return this._routeConfig; }
/**
 * The root of the router state
 * @return {?}
 */
get root(): ActivatedRouteSnapshot { return this._routerState.root; }
/**
 * The parent of this route in the router state tree
 * @return {?}
 */
get parent(): ActivatedRouteSnapshot|null { return this._routerState.parent(this); }
/**
 * The first child of this route in the router state tree
 * @return {?}
 */
get firstChild(): ActivatedRouteSnapshot|null { return this._routerState.firstChild(this); }
/**
 * The children of this route in the router state tree
 * @return {?}
 */
get children(): ActivatedRouteSnapshot[] { return this._routerState.children(this); }
/**
 * The path from the root of the router state tree to this route
 * @return {?}
 */
get pathFromRoot(): ActivatedRouteSnapshot[] { return this._routerState.pathFromRoot(this); }
/**
 * @return {?}
 */
get paramMap(): ParamMap {
    if (!this._paramMap) {
      this._paramMap = convertToParamMap(this.params);
    }
    return this._paramMap;
  }
/**
 * @return {?}
 */
get queryParamMap(): ParamMap {
    if (!this._queryParamMap) {
      this._queryParamMap = convertToParamMap(this.queryParams);
    }
    return this._queryParamMap;
  }
/**
 * @return {?}
 */
toString(): string {
    const /** @type {?} */ url = this.url.map(segment => segment.toString()).join('/');
    const /** @type {?} */ matched = this._routeConfig ? this._routeConfig.path : '';
    return `Route(url:'${url}', path:'${matched}')`;
  }
}

function ActivatedRouteSnapshot_tsickle_Closure_declarations() {
/**
 * \@internal *
 * @type {?}
 */
ActivatedRouteSnapshot.prototype._routeConfig;
/**
 * \@internal *
 * @type {?}
 */
ActivatedRouteSnapshot.prototype._urlSegment;
/**
 * \@internal
 * @type {?}
 */
ActivatedRouteSnapshot.prototype._lastPathIndex;
/**
 * \@internal
 * @type {?}
 */
ActivatedRouteSnapshot.prototype._resolve;
/**
 * \@internal
 * @type {?}
 */
ActivatedRouteSnapshot.prototype._resolvedData;
/**
 * \@internal
 * @type {?}
 */
ActivatedRouteSnapshot.prototype._routerState;
/**
 * \@internal
 * @type {?}
 */
ActivatedRouteSnapshot.prototype._paramMap;
/**
 * \@internal
 * @type {?}
 */
ActivatedRouteSnapshot.prototype._queryParamMap;
/**
 * The URL segments matched by this route
 * @type {?}
 */
ActivatedRouteSnapshot.prototype.url;
/**
 * The matrix parameters scoped to this route
 * @type {?}
 */
ActivatedRouteSnapshot.prototype.params;
/**
 * The query parameters shared by all the routes
 * @type {?}
 */
ActivatedRouteSnapshot.prototype.queryParams;
/**
 * The URL fragment shared by all the routes
 * @type {?}
 */
ActivatedRouteSnapshot.prototype.fragment;
/**
 * The static and resolved data of this route
 * @type {?}
 */
ActivatedRouteSnapshot.prototype.data;
/**
 * The outlet name of the route
 * @type {?}
 */
ActivatedRouteSnapshot.prototype.outlet;
/**
 * The component of the route
 * @type {?}
 */
ActivatedRouteSnapshot.prototype.component;
}

/**
 * \@whatItDoes Represents the state of the router at a moment in time.
 * 
 * \@howToUse 
 * 
 * ```
 * \@Component({templateUrl:'template.html'}) 
 * class MyComponent {
 *   constructor(router: Router) {
 *     const state: RouterState = router.routerState;
 *     const snapshot: RouterStateSnapshot = state.snapshot;
 *     const root: ActivatedRouteSnapshot = snapshot.root;
 *     const child = root.firstChild;
 *     const id: Observable<string> = child.params.map(p => p.id);
 *     //...
 *   }
 * }
 * ```
 * 
 * \@description 
 * RouterStateSnapshot is a tree of activated route snapshots. Every node in this tree knows about
 * the "consumed" URL segments, the extracted parameters, and the resolved data.
 * 
 * \@stable
 */
export class RouterStateSnapshot extends Tree<ActivatedRouteSnapshot> {
/**
 * \@internal
 * @param {?} url
 * @param {?} root
 */
constructor(
public url: string, root: TreeNode<ActivatedRouteSnapshot>) {
    super(root);
    setRouterState(<RouterStateSnapshot>this, root);
  }
/**
 * @return {?}
 */
toString(): string { return serializeNode(this._root); }
}

function RouterStateSnapshot_tsickle_Closure_declarations() {
/**
 * The url from which this snapshot was created
 * @type {?}
 */
RouterStateSnapshot.prototype.url;
}

/**
 * @template U, T
 * @param {?} state
 * @param {?} node
 * @return {?}
 */
function setRouterState<U, T extends{_routerState: U}>(state: U, node: TreeNode<T>): void {
  node.value._routerState = state;
  node.children.forEach(c => setRouterState(state, c));
}
/**
 * @param {?} node
 * @return {?}
 */
function serializeNode(node: TreeNode<ActivatedRouteSnapshot>): string {
  const /** @type {?} */ c = node.children.length > 0 ? ` { ${node.children.map(serializeNode).join(", ")} } ` : '';
  return `${node.value}${c}`;
}
/**
 * The expectation is that the activate route is created with the right set of parameters.
 * So we push new values into the observables only when they are not the initial values.
 * And we detect that by checking if the snapshot field is set.
 * @param {?} route
 * @return {?}
 */
export function advanceActivatedRoute(route: ActivatedRoute): void {
  if (route.snapshot) {
    const /** @type {?} */ currentSnapshot = route.snapshot;
    const /** @type {?} */ nextSnapshot = route._futureSnapshot;
    route.snapshot = nextSnapshot;
    if (!shallowEqual(currentSnapshot.queryParams, nextSnapshot.queryParams)) {
      ( /** @type {?} */((<any>route.queryParams))).next(nextSnapshot.queryParams);
    }
    if (currentSnapshot.fragment !== nextSnapshot.fragment) {
      ( /** @type {?} */((<any>route.fragment))).next(nextSnapshot.fragment);
    }
    if (!shallowEqual(currentSnapshot.params, nextSnapshot.params)) {
      ( /** @type {?} */((<any>route.params))).next(nextSnapshot.params);
    }
    if (!shallowEqualArrays(currentSnapshot.url, nextSnapshot.url)) {
      ( /** @type {?} */((<any>route.url))).next(nextSnapshot.url);
    }
    if (!shallowEqual(currentSnapshot.data, nextSnapshot.data)) {
      ( /** @type {?} */((<any>route.data))).next(nextSnapshot.data);
    }
  } else {
    route.snapshot = route._futureSnapshot;

    // this is for resolved data
    ( /** @type {?} */((<any>route.data))).next(route._futureSnapshot.data);
  }
}
/**
 * @param {?} a
 * @param {?} b
 * @return {?}
 */
export function equalParamsAndUrlSegments(
    a: ActivatedRouteSnapshot, b: ActivatedRouteSnapshot): boolean {
  const /** @type {?} */ equalUrlParams = shallowEqual(a.params, b.params) && equalSegments(a.url, b.url);
  const /** @type {?} */ parentsMismatch = !a.parent !== !b.parent;

  return equalUrlParams && !parentsMismatch &&
      (!a.parent || equalParamsAndUrlSegments(a.parent, /** @type {?} */(( b.parent))));
}